/* istanbul ignore file */
import { FoundationEventBase, FoundationEventBaseOptions } from '../event-emitter/unfiltered';
import { AdClip, AdSlot } from './ad-slot';
import { AdvertisingEvent } from './event-types';

export class BaseAdvertisingEvent<EventType = unknown> extends FoundationEventBase<EventType> {
  constructor(type: AdvertisingEvent, opts: FoundationEventBaseOptions<EventType> = {}) {
    super(type, opts);
  }
}

interface AdErrorOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  code: string;
  message: string;
}

export class AdError<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  public code: string;
  public message: string;

  constructor(
    type: AdvertisingEvent,
    public opts: AdErrorOptions<EventType>,
  ) {
    super(type, opts);

    this.code = opts.code;
    this.message = opts.message;
  }
}

// ADVERTISING: NEW EVENTS

interface AdSlotsCreatedOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  adSlots: Map<number, AdSlot>;
}

export class AdSlotsCreated<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  public adSlots: Map<number, AdSlot>;

  constructor(opts: AdSlotsCreatedOptions<EventType>) {
    super(AdvertisingEvent.AdSlotsCreated, opts);

    this.adSlots = opts.adSlots;
  }
}

interface AdSlotEventOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  adSlot: AdSlot;
}

export class AdSlotEvent<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  public adSlot: AdSlot;

  constructor(type: AdvertisingEvent, opts: AdSlotEventOptions<EventType>) {
    super(type, opts);

    this.adSlot = opts.adSlot;
  }
}

interface AdClipEventOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  adClip: AdClip;
}

export class AdClipEvent<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  public adClip: AdClip;

  constructor(type: AdvertisingEvent, opts: AdClipEventOptions<EventType>) {
    super(type, opts);

    this.adClip = opts.adClip;
  }
}

interface InitAdSlotOptions<EventType> extends AdSlotEventOptions<EventType> {
  autoplay: boolean;
}

export class InitAdSlot<EventType = unknown> extends AdSlotEvent<EventType> {
  public autoplay: boolean;

  constructor(opts: InitAdSlotOptions<EventType>) {
    super(AdvertisingEvent.InitAdSlot, opts);

    this.autoplay = opts.autoplay;
  }
}

interface StopAdSlotOptions<EventType> extends AdSlotEventOptions<EventType> {
  reason: unknown;
}

export class StopAdSlot<EventType = unknown> extends AdSlotEvent<EventType> {
  public reason: unknown;

  constructor(opts: StopAdSlotOptions<EventType>) {
    super(AdvertisingEvent.StopAdSlot, opts);

    this.reason = opts.reason;
  }
}

export class PauseAd<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  constructor(opts: FoundationEventBaseOptions<EventType> = {}) {
    super(AdvertisingEvent.PauseAd, opts);
  }
}

export class ResumeAd<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  constructor(opts: FoundationEventBaseOptions<EventType> = {}) {
    super(AdvertisingEvent.ResumeAd, opts);
  }
}

export class SkipAd<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  constructor(opts: FoundationEventBaseOptions<EventType> = {}) {
    super(AdvertisingEvent.SkipAd, opts);
  }
}

export class OpenClickThrough<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  constructor(opts: FoundationEventBaseOptions<EventType> = {}) {
    super(AdvertisingEvent.OpenClickThrough, opts);
  }
}

interface HomadActivationOptions<EventType> extends AdSlotEventOptions<EventType> {
  reason: number;
}

export class HomadActivation<EventType = unknown> extends AdSlotEvent<EventType> {
  public reason: number;

  constructor(opts: HomadActivationOptions<EventType>) {
    super(AdvertisingEvent.HomadActivation, opts);

    this.reason = opts.reason;
  }
}

export class HomadPenalty<EventType = unknown> extends AdSlotEvent<EventType> {
  constructor(opts: AdSlotEventOptions<EventType>) {
    super(AdvertisingEvent.HomadPenalty, opts);
  }
}

interface AdQuartileReachedOptions<EventType> extends FoundationEventBaseOptions<EventType> {
  quartile: number;
}

export class AdQuartileReachedEvent<EventType = unknown> extends BaseAdvertisingEvent<EventType> {
  public quartile: number;

  constructor(opts: AdQuartileReachedOptions<EventType>) {
    super(AdvertisingEvent.AdQuartileReached, opts);

    this.quartile = opts.quartile;
  }
}
